import {
  faCheckCircle,
  faCreditCard,
  faExclamationCircle,
  faPlusCircle,
  faShieldAlt,
  faTrashAlt,
  faExclamationTriangle,
  faExchangeAlt,
  faTag,
  faTimes,
  faSyncAlt,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import segmentHelper from 'helpers/segment-helper';
import { capitalize } from 'helpers/utils';
import moment from 'moment';
import React, { Suspense, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { refreshAllAccountInfo } from 'stores/app';
import {
  paymentMethodDelete,
  paymentMethodGet,
  markPrimaryAndBackupPaymentMethod
} from 'stores/subscription';
import api from 'util/api';
import logger from 'util/logger';
import _ from 'lodash';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import enums from 'util/enums';

const AddPaymentCard = React.lazy(() =>
  import('components/stripe/AddPaymnetCard')
);

const PaymentMethodStripe = () => {
  const { paymentCards } = useSelector(state => state.subscription);
  const [loadingPaymentCards, setLoadingPaymentCards] = useState(false);
  const [removingPaymentMethod, setRemovingPaymentMethod] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [addCardMessage, setAddCardMessage] = useState(null);
  const [paymentCardDisplay, setPaymentCardDisplay] = useState(null);
  const [settingPrimaryStatus, setSettingPrimaryStatus] = useState(null);
  const [settingBackupStatus, setSettingBackupStatus] = useState(null);
  const [removingCardStatus, setRemovingCardStatus] = useState(null);
  const [swappingCards, setSwappingCards] = useState(false);
  const [notificationData, setNotificationData] = useState(null);

  useEffect(() => {
    setPaymentCardDisplay(paymentCards);
    if (paymentCards && paymentCards.length === 1) {
      // Set the first card as primary card if it is not already set
      if (!paymentCards[0].IsDefaultPaymentMethod) {
        setAsPrimaryAndBackupCard(paymentCards[0], true, false);
      }
    }
  }, [paymentCards]);

  const dispatch = useDispatch();

  const initPaymentCards = () => {
    setLoadingPaymentCards(true);
    api
      .Enumeration(enums.ENUMERATION_TYPES.PAYMENTCARD, {})
      .then(res => {
        const objects = res?.Objects || [];
        setPaymentCardDisplay(objects);
        setLoadingPaymentCards(false);
      })
      .catch(() => {
        setLoadingPaymentCards(false);
      });
  };

  const fetchDiscountAmount = async () => {
    const response = await api.creditcardsNotifications.list();
    // Handle notifications data
    if (response && response.DiscountPercentage) {
      setNotificationData(response);
    }
  };

  useEffect(() => {
    initPaymentCards();
    fetchDiscountAmount();
  }, []);

  const isCardExpired = (expMonth, expYear) => {
    const currentDate = moment();
    const cardExpiry = moment(`${expYear}-${expMonth}`, 'YYYY-MM').endOf(
      'month'
    );
    return currentDate.isAfter(cardExpiry);
  };

  const confirmRemovePaymentMethod = pm_id => {
    confirmAlert({
      //title: 'Confirmation',
      message: 'Are you sure you want to delete this payment method?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => proceedToDeletePaymentMethod(pm_id)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const proceedToDeletePaymentMethod = pm_id => {
    setRemovingPaymentMethod(pm_id);
    dispatch(paymentMethodDelete(pm_id))
      .then(res => {
        dispatch(paymentMethodGet());
        setRemovingPaymentMethod(null);
        segmentHelper.track(segmentHelper.events.PAYMENT_METHOD_REMOVED);
        toast.success(res.Message || 'Payment method removed successfully.', {
          theme: 'colored',
          position: 'bottom-center',
          icon: faCheckCircle,
          toastId: 'payment-methhod-remove'
        });
      })
      .catch(err => {
        toast.warning(
          err?.Data?.Details ||
            'Unable to remove this payment method. try again',
          {
            theme: 'colored',
            position: 'bottom-center',
            icon: faExclamationCircle,
            toastId: 'payment-methhod-remove'
          }
        );
        setRemovingPaymentMethod(null);
      });
  };

  const setResponseMessage = (message, variant) => {
    setAddCardMessage(
      message
        ? { message: capitalize(message), variant: variant || 'primary' }
        : null
    );
  };

  const addPaymentMethod = () => {
    setResponseMessage();
    setModalShow(true);
  };

  const cancelAddPaymentMethod = () => {
    setModalShow(false);
    setResponseMessage();
  };

  const handlePaymentIdMethod = async res => {
    setSavingCard(true);
    try {
      new Promise((resolve, reject) => {
        logger.log('handlePaymentIdMethod res', res);
        if (res) resolve(api.PaymentCardV2.create(res.id));
        else reject(false);
      })
        .then(() => {
          dispatch(refreshAllAccountInfo());
          setSavingCard(false);
          cancelAddPaymentMethod();
          initPaymentCards();
          setResponseMessage('Payment card added successfully.', 'success');
          toast.success('Payment card added successfully!', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: 'payment-card-add'
          });
        })
        .catch(() => {
          setSavingCard(false);
        });
    } catch (err) {
      setSavingCard(false);
      logger.log('payment update error', err);
      setResponseMessage(
        err.Message || 'Unable to save payment method. Please try again.',
        'danger'
      );
    }
  };

  // Swap Primary and Backup
  const swapPrimaryBackup = () => {
    setSwappingCards(true);

    const existingPrimaryCard = paymentCardDisplay.find(
      p => p.IsDefaultPaymentMethod
    );
    const existingBackupCard = paymentCardDisplay.find(
      p => p.IsBackupPaymentMethod
    );

    if (existingPrimaryCard && existingBackupCard) {
      // Set existing primary card as backup
      dispatch(
        markPrimaryAndBackupPaymentMethod(existingPrimaryCard.GUID, {
          IsDefaultPaymentMethod: false,
          IsBackupPaymentMethod: true
        })
      )
        .then(() => {
          // Set existing backup card as primary
          dispatch(
            markPrimaryAndBackupPaymentMethod(existingBackupCard.GUID, {
              IsDefaultPaymentMethod: true,
              IsBackupPaymentMethod: false
            })
          ).then(() => {
            dispatch(paymentMethodGet()).then(() => {
              toast.success('Primary and Backup cards swapped', {
                theme: 'colored',
                position: 'bottom-center',
                icon: faExchangeAlt,
                toastId: 'swap-cards'
              });
              setSwappingCards(false);
            });
          });
        })
        .catch(() => {
          setSwappingCards(false);
          toast.error('Failed to swap cards. Please try again.', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faExclamationCircle,
            toastId: 'swap-cards-error'
          });
        });
    }
  };

  // Set card as Primary and Backup
  const setAsPrimaryAndBackupCard = (card, isPrimary, isBackup) => {
    if (isPrimary) {
      setSettingPrimaryStatus(card.GUID);
    } else {
      setSettingBackupStatus(card.GUID);
    }

    const statusConfig = {
      IsDefaultPaymentMethod: isPrimary ? true : card.IsDefaultPaymentMethod,
      IsBackupPaymentMethod: isBackup ? true : card.IsBackupPaymentMethod
    };

    const successMessage = isPrimary
      ? 'Card set as primary successfully'
      : 'Card set as backup successfully';
    const toastId = isPrimary ? 'set-primary' : 'set-backup';

    // Set card as Primary/Backup
    dispatch(markPrimaryAndBackupPaymentMethod(card.GUID, statusConfig))
      .then(() => {
        dispatch(paymentMethodGet()).then(() => {
          toast.success(successMessage, {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: toastId
          });
          if (isPrimary) {
            setSettingPrimaryStatus(null);
          } else {
            setSettingBackupStatus(null);
          }
        });
      })
      .catch(() => {
        if (isPrimary) {
          setSettingPrimaryStatus(null);
        } else {
          setSettingBackupStatus(null);
        }
        toast.error('Failed to update card status. Please try again.', {
          theme: 'colored',
          position: 'bottom-center',
          icon: faExclamationCircle,
          toastId: 'set-primary-error'
        });
      });
  };

  // Remove card from Primary/Backup
  const removeFromPrimaryBackup = (card, isPrimary, isBackup) => {
    setRemovingCardStatus(card.GUID);

    const statusConfig = {
      IsDefaultPaymentMethod: isPrimary ? false : card.IsDefaultPaymentMethod,
      IsBackupPaymentMethod: isBackup ? false : card.IsBackupPaymentMethod
    };
    const successMessage = isPrimary
      ? 'Card removed from primary successfully'
      : 'Card removed from backup successfully';
    const toastId = isPrimary ? 'remove-from-primary' : 'remove-from-backup';

    // Remove card from Primary/Backup
    dispatch(markPrimaryAndBackupPaymentMethod(card.GUID, statusConfig))
      .then(() => {
        dispatch(paymentMethodGet()).then(() => {
          toast.success(successMessage, {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: toastId
          });
          setRemovingCardStatus(null);
        });
      })
      .catch(() => {
        setRemovingCardStatus(null);
        toast.error('Failed to remove card status. Please try again.', {
          theme: 'colored',
          position: 'bottom-center',
          icon: faExclamationCircle,
          toastId: 'remove-from-primary-backup-error'
        });
      });
  };

  // Check if both Primary and Backup cards exist
  const hasBothPrimaryBackup = () => {
    if (paymentCardDisplay) {
      // Filter out expired cards
      const activeCards = paymentCardDisplay.filter(
        p => p.Active && !isCardExpired(p.ExpMonth, p.ExpYear)
      );

      const hasPrimary = activeCards.some(p => p.IsDefaultPaymentMethod);
      const hasBackup = activeCards.some(p => p.IsBackupPaymentMethod);
      const result = hasPrimary && hasBackup;
      return result;
    }
    return false;
  };

  return (
    <>
      <PageHeader
        title="Payment method"
        description="List of payment cards added to your account."
        className="mb-3"
        titleTag="h2"
      ></PageHeader>
      {/* Discount Applied Badge */}
      {notificationData && notificationData?.IsDiscountAvailable && (
        <div
          className={`alert ${
            hasBothPrimaryBackup() ? 'alert-success' : 'alert-warning'
          } d-flex align-items-center mb-3`}
          role="alert"
        >
          <FontAwesomeIcon
            icon={hasBothPrimaryBackup() ? faTag : faExclamationTriangle}
            className="me-2"
          />
          {hasBothPrimaryBackup() ? (
            <>
              <strong>Discount Applied</strong> (Because you set a primary and a
              backup card, you are receiving a{' '}
              {notificationData?.DiscountPercentage}% discount on your next
              billing cycle)
            </>
          ) : (
            `To unlock your ${notificationData?.DiscountPercentage}% discount, please set a primary and a backup card.`
          )}
        </div>
      )}

      {/* Dashboard Count Cards */}
      <Row className="mb-3 g-3">
        <Col xs={6} sm={4} md={2} lg={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center p-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className="text-primary fs-2 me-2"
                />
                <h3 className="mb-0 fw-bold text-dark">
                  {paymentCardDisplay?.length}
                </h3>
              </div>
              <h6 className="mb-0">Total Cards</h6>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} sm={4} md={2} lg={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center p-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success fs-2 me-2"
                />
                <h3 className="mb-0 fw-bold text-dark">
                  {
                    _.filter(paymentCardDisplay, card => card.Active).filter(
                      card => !isCardExpired(card.ExpMonth, card.ExpYear)
                    ).length
                  }
                </h3>
              </div>
              <h6 className="mb-0">Active Cards</h6>
            </Card.Body>
          </Card>
        </Col>

        {/* <Col xs={6} sm={4} md={2} lg={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center p-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-warning fs-2 me-2"
                />
                <h3 className="mb-0 fw-bold text-dark">
                  {
                    _.filter(paymentCardDisplay, card =>
                      isCardExpired(card.ExpMonth, card.ExpYear)
                    ).length
                  }
                </h3>
              </div>
              <h6 className="mb-0">Expired Cards</h6>
            </Card.Body>
          </Card>
        </Col> */}

        <Col xs={6} sm={4} md={2} lg={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center p-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  className="text-info fs-2 me-2"
                />
                <h3 className="mb-0 fw-bold text-dark">
                  {
                    _.filter(paymentCardDisplay, p => p.IsDefaultPaymentMethod)
                      .length
                  }
                </h3>
              </div>
              <h6 className="mb-0">Primary Cards</h6>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} sm={4} md={2} lg={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center p-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon
                  icon={faSyncAlt}
                  className="text-secondary fs-2 me-2"
                />
                <h3 className="mb-0 fw-bold text-dark">
                  {
                    _.filter(paymentCardDisplay, p => p.IsBackupPaymentMethod)
                      .length
                  }
                </h3>
              </div>
              <h6 className="mb-0">Backup Cards</h6>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              title="Added payment cards"
              endEl={
                <Button onClick={() => addPaymentMethod()} variant="primary">
                  <FontAwesomeIcon icon={faPlusCircle} size="sm" />{' '}
                  <span>Add</span>
                </Button>
              }
            />
            <hr className="mt-0 pt-0" />
            <FalconComponentCard.Body noLight className="py-0">
              {loadingPaymentCards ? (
                <Flex
                  justifyContent={'justify'}
                  direction="column"
                  alignContent="center"
                  alignItems="center"
                  className="text-center my-3 py-3"
                >
                  <Spinner />
                  <p className="mt-3 text-muted">Loading payment method</p>
                </Flex>
              ) : (
                <Col sm={12} md={12} className="pe-lg-2 mb-3">
                  {/* Swap Button */}
                  {hasBothPrimaryBackup() && (
                    <div className="mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={swapPrimaryBackup}
                        disabled={swappingCards}
                      >
                        {swappingCards ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Swapping...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faExchangeAlt}
                              className="me-1"
                            />
                            Swap Primary & Backup
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  <Row>
                    {_.map(paymentCardDisplay, method => {
                      const isExpired = isCardExpired(
                        method.ExpMonth,
                        method.ExpYear
                      );
                      const isPrimary = method.IsDefaultPaymentMethod;
                      const isBackup = method.IsBackupPaymentMethod;
                      const isActive = method.Active;

                      return (
                        <Col
                          key={method.GUID}
                          sm={12}
                          md={6}
                          lg={6}
                          xl={4}
                          className="my-3"
                        >
                          <div className="position-relative">
                            <Card
                              bg={
                                isExpired
                                  ? 'soft-primary'
                                  : method.Active
                                  ? 'soft-success'
                                  : method.is_valid_pm
                                  ? 'primary'
                                  : 'secondary'
                              }
                              text="dark"
                              className={isExpired ? 'border-primary' : ''}
                            >
                              <Card.Body>
                                <Card.Title as="div">
                                  <Flex justifyContent="between fs--1">
                                    {/* Primary/Backup Action Buttons */}
                                    <>
                                      {!isPrimary && !isBackup && (
                                        <div className="d-flex gap-1">
                                          {/* Check if card is expired */}
                                          {isExpired ? (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>
                                                  You can&apos;t set a primary
                                                  as card has expired.
                                                </Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant="outline-primary"
                                                  disabled={true}
                                                >
                                                  Set Primary
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          ) : !isActive ? (
                                            // Check if card is inactive
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>
                                                  You can&apos;t set a primary
                                                  as card is inactive.
                                                </Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant="outline-primary"
                                                  disabled={true}
                                                >
                                                  Set Primary
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="outline-primary"
                                              onClick={() =>
                                                setAsPrimaryAndBackupCard(
                                                  method,
                                                  true,
                                                  false
                                                )
                                              }
                                              disabled={
                                                settingPrimaryStatus ===
                                                method.GUID
                                              }
                                            >
                                              {settingPrimaryStatus ===
                                              method.GUID ? (
                                                <>
                                                  <Spinner
                                                    size="sm"
                                                    className="me-1"
                                                  />
                                                  Setting...
                                                </>
                                              ) : (
                                                'Set Primary'
                                              )}
                                            </Button>
                                          )}
                                          {isExpired ? (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>
                                                  You can&apos;t set a backup as
                                                  card has expired.
                                                </Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  disabled={true}
                                                >
                                                  Set Backup
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          ) : !isActive ? (
                                            // Check if card is inactive
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>
                                                  You can&apos;t set a backup as
                                                  card is inactive.
                                                </Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  disabled={true}
                                                >
                                                  Set Backup
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() =>
                                                setAsPrimaryAndBackupCard(
                                                  method,
                                                  false,
                                                  true
                                                )
                                              }
                                              disabled={
                                                settingBackupStatus ===
                                                method.GUID
                                              }
                                            >
                                              {settingBackupStatus ===
                                              method.GUID ? (
                                                <>
                                                  <Spinner
                                                    size="sm"
                                                    className="me-1"
                                                  />
                                                  Setting...
                                                </>
                                              ) : (
                                                'Set Backup'
                                              )}
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                      {(isPrimary || isBackup) && (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() =>
                                            removeFromPrimaryBackup(
                                              method,
                                              isPrimary,
                                              isBackup
                                            )
                                          }
                                          disabled={
                                            removingCardStatus === method.GUID
                                          }
                                        >
                                          {removingCardStatus ===
                                          method.GUID ? (
                                            <>
                                              <Spinner
                                                size="sm"
                                                className="me-1"
                                              />
                                              Removing...
                                            </>
                                          ) : (
                                            <>
                                              <FontAwesomeIcon
                                                icon={faTimes}
                                                className="me-1"
                                              />
                                              Remove from{' '}
                                              {isPrimary ? 'Primary' : 'Backup'}
                                            </>
                                          )}
                                        </Button>
                                      )}
                                    </>
                                    {/* Primary Badge */}
                                    {isPrimary ? (
                                      <span className="p-2 badge bg-primary">
                                        <FontAwesomeIcon
                                          icon={faShieldAlt}
                                          className="me-1"
                                        />
                                        Primary
                                      </span>
                                    ) : null}
                                    {/* Backup Badge */}
                                    {isBackup ? (
                                      <span className="p-2 badge bg-secondary">
                                        <FontAwesomeIcon
                                          icon={faSyncAlt}
                                          className="me-1"
                                        />
                                        Backup
                                      </span>
                                    ) : null}
                                    {/* Expired Badge */}
                                    {isExpired ? (
                                      <span className="p-2 badge bg-warning text-white">
                                        <FontAwesomeIcon
                                          icon={faExclamationTriangle}
                                          className="me-1 text-white"
                                        />
                                        Expired
                                      </span>
                                    ) : null}
                                  </Flex>
                                </Card.Title>
                                {/* <div>
                                      {changingDefaultMethod === method.GUID ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <Form.Check
                                          size={'sm'}
                                          type="switch"
                                          disabled={!method.Active || isExpired}
                                          onChange={() =>
                                            markDefaultPaymentMethod(method)
                                          }
                                          id={'method-switch' + method.GUID}
                                          checked={
                                            method.IsDefaultPaymentMethod
                                          }
                                          defaultChecked={
                                            method.IsDefaultPaymentMethod
                                              ? true
                                              : false
                                          }
                                        />
                                      )}
                                    </div> */}
                                <Flex justifyContent="between fs--1">
                                  <div>
                                    <span className="fs-0">xxxx xxxx xxxx</span>{' '}
                                    <span className="fs-2">
                                      {method.CardLast4}
                                    </span>
                                  </div>
                                  <Card.Text className="text-end">
                                    {method.CardType}
                                  </Card.Text>
                                </Flex>

                                <Flex justifyContent="between fs--1">
                                  <Card.Text>
                                    {method.Active ? (
                                      <div>
                                        {isExpired ? (
                                          <div className="text-warning">
                                            <FontAwesomeIcon
                                              icon={faExclamationCircle}
                                            />{' '}
                                            Expired
                                          </div>
                                        ) : (
                                          <div>
                                            <FontAwesomeIcon
                                              icon={faCheckCircle}
                                            />{' '}
                                            Active
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-warning">
                                        <FontAwesomeIcon icon={faBan} />{' '}
                                        Inactive
                                      </div>
                                    )}
                                  </Card.Text>
                                </Flex>
                                <Flex justifyContent="between fs--1">
                                  <Card.Text>
                                    {method.ExpMonth} / {method.ExpYear}
                                  </Card.Text>
                                </Flex>
                                <Flex justifyContent="between fs--1 mt-3">
                                  {method.Active ? (
                                    <Card.Text className="mb-0 mt-1">
                                      {removingPaymentMethod === method.GUID ? (
                                        <span>
                                          <Spinner size="sm" />
                                        </span>
                                      ) : (
                                        <div>
                                          <FontAwesomeIcon
                                            icon={faTrashAlt}
                                            className="cursor-pointer"
                                            onClick={() => {
                                              confirmRemovePaymentMethod(
                                                method.GUID
                                              );
                                            }}
                                          />
                                        </div>
                                      )}
                                    </Card.Text>
                                  ) : (
                                    <div> </div>
                                  )}
                                  <Card.Text className="text-end mb-0">
                                    Added on{' '}
                                    <span className="fs-1">
                                      {moment(method.Created)
                                        .local()
                                        .format('MM/DD/YYYY')}
                                    </span>
                                  </Card.Text>
                                </Flex>
                              </Card.Body>
                            </Card>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              )}

              {!loadingPaymentCards && paymentCardDisplay?.length == 0 && (
                <Col sm={12} className="text-center text-muted">
                  <p>
                    <FontAwesomeIcon icon={faCreditCard} size="2x" />
                  </p>
                  <p>You have not added payment method yet.</p>
                </Col>
              )}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      <Modal
        show={modalShow}
        onHide={() => cancelAddPaymentMethod()}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">Add Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col sm={12}>
              <Suspense
                fallback={
                  <Flex justifyContent="center" className="my-3">
                    <Spinner />
                  </Flex>
                }
              >
                <AddPaymentCard
                  handlePaymentIdMethod={handlePaymentIdMethod}
                  isSaving={savingCard}
                  setSaving={setSavingCard}
                />
              </Suspense>
            </Col>
            {addCardMessage && (
              <div className="flex-1 mt-1">
                <Alert variant={addCardMessage.variant}>
                  <p className="py-0 my-0">{addCardMessage.message}</p>
                </Alert>
              </div>
            )}
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PaymentMethodStripe;
